import { apiService } from '../services/api';
import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { PublicHeader } from '../components/layout/PublicHeader';
import { PublicFooter } from '../components/layout/PublicFooter';
import { ShieldCheckIcon, CloudIcon, ComputerDesktopIcon, BoltIcon, CubeIcon, BeakerIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../constants/routes';

export const LandingPage: React.FC = () => {
    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const id = hash.replace('#', '');
            const element = document.getElementById(id);
            if (element) {
                // Timeout to ensure DOM is fully rendered
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        } else {
            window.scrollTo(0, 0);
        }
    }, [hash]);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.id]: e.target.value
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');

        try {
            await apiService.post('/contact/', formData);
            setStatus('success');
            setFormData({ name: '', email: '', subject: '', message: '' });
        } catch (error) {
            console.error(error);
            setStatus('error');
        }
    };

    return (
        <div className="bg-background-dark min-h-screen text-white font-body selection:bg-brand/30 selection:text-white">
            <PublicHeader />

            {/* Hero Section */}
            <section id="home" className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-brand/20 blur-[120px]"></div>
                    <div className="absolute bottom-[10%] right-[-5%] w-[600px] h-[600px] rounded-full bg-purple-600/10 blur-[150px]"></div>
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <h1 className="text-5xl md:text-7xl font-bold font-display tracking-tight mb-6 leading-tight">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">Fortalece tu</span> <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand to-purple-500">Futuro Digital</span>
                    </h1>
                    <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-400 mb-10 leading-relaxed">
                        Soluciones tecnológicas avanzadas en seguridad, desarrollo de software y sistemas empresariales. Protegemos lo que más importa con tecnología de punta y supervisión experta.
                    </p>
                    <div className="flex justify-center gap-4">
                        <a href="#services" className="px-8 py-3 rounded-lg bg-brand hover:bg-brand/90 text-white font-bold text-lg transition-all shadow-[0_0_30px_-10px_rgba(19,91,236,0.5)]">
                            Explorar Soluciones
                        </a>
                        <a href="#contact" className="px-8 py-3 rounded-lg border border-white/10 hover:bg-white/5 text-white font-bold text-lg transition-all backdrop-blur-sm">
                            Comenzar
                        </a>
                    </div>

                    <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-3 lg:grid-cols-3">
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                            <div className="text-3xl font-bold text-white mb-1">99.9%</div>
                            <div className="text-sm text-gray-400 uppercase tracking-wider font-medium">Disponibilidad</div>
                        </div>
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                            <div className="text-3xl font-bold text-white mb-1">24/7</div>
                            <div className="text-sm text-gray-400 uppercase tracking-wider font-medium">Soporte Dedicado</div>
                        </div>
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                            <div className="text-3xl font-bold text-white mb-1">&lt;60s</div>
                            <div className="text-sm text-gray-400 uppercase tracking-wider font-medium">Tiempo de Respuesta</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section id="services" className="py-24 bg-background-dark relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-4">Nuestros Servicios</h2>
                        <p className="text-gray-400 max-w-2xl mx-auto">Soluciones integrales adaptadas a sus necesidades</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Service 1 */}
                        <div className="bg-[#161b22] border border-white/5 p-8 rounded-2xl hover:border-brand/50 transition-colors group">
                            <div className="size-12 bg-brand/10 rounded-lg flex items-center justify-center text-brand mb-6 group-hover:scale-110 transition-transform">
                                <ShieldCheckIcon className="size-7" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">Seguridad</h3>
                            <p className="text-gray-400 mb-6 leading-relaxed">
                                Protección avanzada contra amenazas, auditorías de seguridad, y gestión de vulnerabilidades con monitoreo 24/7.
                            </p>
                            <Link to={ROUTES.SECURITY} className="text-brand font-bold text-sm hover:underline flex items-center gap-1">
                                Ver más <span>→</span>
                            </Link>
                        </div>

                        {/* Service 2 */}
                        <div className="bg-[#161b22] border border-white/5 p-8 rounded-2xl hover:border-brand/50 transition-colors group">
                            <div className="size-12 bg-brand/10 rounded-lg flex items-center justify-center text-brand mb-6 group-hover:scale-110 transition-transform">
                                <CloudIcon className="size-7" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">Soluciones TI</h3>
                            <p className="text-gray-400 mb-6 leading-relaxed">
                                Infraestructura tecnológica, soporte técnico especializado, y consultoría para transformación digital empresarial.
                            </p>
                            <Link to={ROUTES.IT_SOLUTIONS} className="text-brand font-bold text-sm hover:underline flex items-center gap-1">
                                Ver más <span>→</span>
                            </Link>
                        </div>

                        {/* Service 3 */}
                        <div className="bg-[#161b22] border border-white/5 p-8 rounded-2xl hover:border-brand/50 transition-colors group">
                            <div className="size-12 bg-brand/10 rounded-lg flex items-center justify-center text-brand mb-6 group-hover:scale-110 transition-transform">
                                <ComputerDesktopIcon className="size-7" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">Desarrollo de Software</h3>
                            <p className="text-gray-400 mb-6 leading-relaxed">
                                Desarrollo a medida de aplicaciones web, móviles y sistemas empresariales con las últimas tecnologías.
                            </p>
                            <Link to={ROUTES.SOFTWARE_DEVELOPMENT} className="text-brand font-bold text-sm hover:underline flex items-center gap-1">
                                Ver más <span>→</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Software Section */}
            <section id="software" className="py-24 bg-[#0d1117]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-4">Nuestro Software</h2>
                        <p className="text-gray-400 max-w-2xl mx-auto">Sistemas especializados desarrollados para optimizar su negocio</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                        {/* Software 1 */}
                        <div className="bg-background-dark border border-white/5 p-8 rounded-2xl flex flex-col">
                            <div className="size-12 bg-purple-500/10 rounded-lg flex items-center justify-center text-purple-400 mb-6">
                                <BoltIcon className="size-7" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Sistema de Notificación</h3>
                            <p className="text-gray-400 mb-6 flex-grow">
                                Plataforma avanzada de gestión de notificaciones multicanal con alertas en tiempo real, programación inteligente y análisis de entrega.
                            </p>
                            <div className="flex flex-wrap gap-2 mb-6">
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Email</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">SMS</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Push</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Webhooks</span>
                            </div>
                            <a href="#contact" className="text-center w-full py-2 rounded-lg border border-white/10 hover:bg-white/5 text-sm font-bold transition-colors">
                                Solicitar Demo
                            </a>
                        </div>

                        {/* Software 2 */}
                        <div className="bg-background-dark border border-white/5 p-8 rounded-2xl flex flex-col">
                            <div className="size-12 bg-blue-500/10 rounded-lg flex items-center justify-center text-blue-400 mb-6">
                                <CubeIcon className="size-7" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Sistema de Almacén</h3>
                            <p className="text-gray-400 mb-6 flex-grow">
                                Control integral de inventarios con gestión de stock, trazabilidad completa, kardex automatizado y reportes en tiempo real.
                            </p>
                            <div className="flex flex-wrap gap-2 mb-6">
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Inventario</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Kardex</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Lotes</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Reportes</span>
                            </div>
                            <a href="#contact" className="text-center w-full py-2 rounded-lg border border-white/10 hover:bg-white/5 text-sm font-bold transition-colors">
                                Solicitar Demo
                            </a>
                        </div>

                        {/* Software 3 */}
                        <div className="bg-background-dark border border-white/5 p-8 rounded-2xl flex flex-col">
                            <div className="size-12 bg-green-500/10 rounded-lg flex items-center justify-center text-green-400 mb-6">
                                <BeakerIcon className="size-7" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Sistema de Análisis Organoléptico</h3>
                            <p className="text-gray-400 mb-6 flex-grow">
                                Evaluación sensorial profesional para control de calidad con paneles de catación, análisis estadísticos y certificación de productos.
                            </p>
                            <div className="flex flex-wrap gap-2 mb-6">
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Catación</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Calidad</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Análisis</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Certificación</span>
                            </div>
                            <a href="#contact" className="text-center w-full py-2 rounded-lg border border-white/10 hover:bg-white/5 text-sm font-bold transition-colors">
                                Solicitar Demo
                            </a>
                        </div>

                        {/* Software 4 */}
                        <div className="bg-background-dark border border-white/5 p-8 rounded-2xl flex flex-col">
                            <div className="size-12 bg-yellow-500/10 rounded-lg flex items-center justify-center text-yellow-400 mb-6">
                                <ChartBarIcon className="size-7" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Sistema de Café</h3>
                            <p className="text-gray-400 mb-6 flex-grow">
                                Gestión completa para cafeterías y tostadurías con control de producción, trazabilidad del grano, ventas y programa de fidelización.
                            </p>
                            <div className="flex flex-wrap gap-2 mb-6">
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Producción</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Trazabilidad</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">POS</span>
                                <span className="px-3 py-1 bg-white/5 rounded-full text-xs font-medium text-gray-300 border border-white/10">Fidelización</span>
                            </div>
                            <a href="#contact" className="text-center w-full py-2 rounded-lg border border-white/10 hover:bg-white/5 text-sm font-bold transition-colors">
                                Solicitar Demo
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-24 bg-background-dark relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">Acerca de FortifyTech</h2>
                            <p className="text-gray-400 text-lg leading-relaxed mb-8">
                                Con años de experiencia en tecnología y seguridad, FortifyTech se posiciona como líder en soluciones tecnológicas integrales. Combinamos innovación, experiencia y compromiso para entregar soluciones que transforman negocios.
                            </p>
                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="size-6 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold shrink-0">✓</div>
                                    <div>
                                        <h4 className="text-white font-bold text-lg">Líderes en Tecnología</h4>
                                        <p className="text-gray-400">Confianza de empresas líderes en Perú.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="size-6 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold shrink-0">✓</div>
                                    <div>
                                        <h4 className="text-white font-bold text-lg">Expertos Certificados</h4>
                                        <p className="text-gray-400">Equipo de profesionales especializados.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="size-6 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold shrink-0">✓</div>
                                    <div>
                                        <h4 className="text-white font-bold text-lg">Soporte 24/7</h4>
                                        <p className="text-gray-400">Monitoreo y soporte continuo.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="aspect-square rounded-full bg-gradient-to-tr from-brand to-purple-600 opacity-20 blur-[100px] absolute inset-0"></div>
                            <div className="relative bg-[#161b22] border border-white/10 rounded-2xl p-8 transform rotate-3 hover:rotate-0 transition-all duration-500">
                                <div className="absolute top-0 right-0 p-4 opacity-50">
                                    <ShieldCheckIcon className="size-32 text-white/5" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-4">Nuestra Misión</h3>
                                <p className="text-gray-400 leading-relaxed">
                                    Empoderar a las empresas con soluciones tecnológicas seguras, escalables e innovadoras que impulsen el crecimiento y la resiliencia en un mundo digital.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-24 bg-[#0d1117]">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-4">Contáctanos</h2>
                        <p className="text-gray-400">¿Listo para transformar tu empresa con tecnología? Ponte en contacto hoy.</p>
                    </div>

                    <div className="bg-background-dark border border-white/10 rounded-2xl p-8 md:p-12 shadow-2xl">
                        {status === 'success' ? (
                            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6 text-center">
                                <div className="mx-auto flex items-center justify-center size-12 rounded-full bg-green-500/20 mb-4">
                                    <span className="material-symbols-outlined text-green-400 text-3xl">check_circle</span>
                                </div>
                                <h3 className="text-lg font-bold text-green-400 mb-2">¡Mensaje Enviado!</h3>
                                <p className="text-gray-300">
                                    Gracias por contactarnos. Nos pondremos en contacto con usted en breve.
                                </p>
                                <button
                                    onClick={() => setStatus('idle')}
                                    className="mt-6 text-sm font-medium text-brand hover:text-brand/80 underline"
                                >
                                    Enviar otro mensaje
                                </button>
                            </div>
                        ) : (
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                {status === 'error' && (
                                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">
                                        Error al enviar el mensaje. Por favor intente nuevamente.
                                    </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label htmlFor="name" className="text-sm font-medium text-gray-300">Nombre</label>
                                        <input
                                            type="text"
                                            id="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full bg-[#161b22] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                            placeholder="Tu Nombre"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label htmlFor="email" className="text-sm font-medium text-gray-300">Correo</label>
                                        <input
                                            type="email"
                                            id="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full bg-[#161b22] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                            placeholder="tu@correo.com"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="subject" className="text-sm font-medium text-gray-300">Asunto</label>
                                    <input
                                        type="text"
                                        id="subject"
                                        required
                                        value={formData.subject}
                                        onChange={handleChange}
                                        className="w-full bg-[#161b22] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                        placeholder="¿Cómo podemos ayudarte?"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="message" className="text-sm font-medium text-gray-300">Mensaje</label>
                                    <textarea
                                        id="message"
                                        required
                                        rows={4}
                                        value={formData.message}
                                        onChange={handleChange}
                                        className="w-full bg-[#161b22] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors"
                                        placeholder="Tu mensaje..."
                                    ></textarea>
                                </div>
                                <button
                                    type="submit"
                                    disabled={status === 'submitting'}
                                    className="w-full py-3 bg-brand hover:bg-brand/90 text-white font-bold rounded-lg shadow-lg shadow-brand/20 transition-all flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {status === 'submitting' ? (
                                        <>
                                            <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                            Enviando...
                                        </>
                                    ) : (
                                        'Enviar Mensaje'
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
};
